import React, { useState } from 'react';
import { Form, Field } from 'react-final-form';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { InputFieldV2Adapter } from '@zola/zola-ui/src/components/Form/InputFieldV2';
import { composeValidators, Validations } from '@zola/zola-ui/src/components/Form/util';
import { EXACT_LENGTH } from 'components/forms/validator';

import ApiService from '@zola-helpers/client/dist/es/http/api';

// interface
import type { WCollectAddressGuestGroupGuestMatchView } from '@zola/svc-web-api-ts-client';

// styles
import styles from './GuestSearchForm.module.less';

const { maxLength: MAX_LENGTH, REQUIRED } = Validations;

interface CoupleSearchFormProps {
  onSubmit: (result: WCollectAddressGuestGroupGuestMatchView | null) => void;
  weddingAccountId?: string;
}

interface Values {
  initial: string;
  lastName: string;
}

const GuestSearchForm: React.FC<CoupleSearchFormProps> = ({ onSubmit, weddingAccountId }) => {
  const [guests, setGuests] = useState<WCollectAddressGuestGroupGuestMatchView[] | undefined>();
  async function submitGuestSearchForm(values: Values) {
    const URL = `${window.location.origin}/web-api/v1/guestgroup/collect_address/search`;
    const result = await ApiService.post<WCollectAddressGuestGroupGuestMatchView[]>(URL, {
      family_name: values.lastName,
      first_initial: values.initial,
      limit: 0,
      offset: 0,
      wedding_account_uuid: weddingAccountId,
    });
    setGuests(result);
    if (result.length === 0) {
      onSubmit(null);
    }
  }

  function renderGuestSelection() {
    if (guests && guests.length > 0) {
      const guestSelection = guests.map(guest => (
        <div key={`${guest.first_name}-${guest.family_name}`} className={styles.listItem}>
          <div>{`${guest.first_name} ${guest.family_name}`} </div>
          <ButtonV3
            variant="secondary"
            onClick={() => {
              onSubmit(guest);
            }}
          >
            Select
          </ButtonV3>
        </div>
      ));
      return (
        <section className={styles.nameList}>
          <h3>Select your name to unlock your private form</h3>
          {guestSelection}
          <div key="no-match" className={styles.listItem}>
            <div>I don&rsquo;t see my name</div>
            <ButtonV3
              variant="secondary"
              onClick={() => {
                onSubmit(null);
              }}
            >
              Add It
            </ButtonV3>
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <Form<Values>
      onSubmit={submitGuestSearchForm}
      render={({ handleSubmit, invalid }) => (
        <form onSubmit={handleSubmit} className={styles.form}>
          <h1 className={styles.header}>Please enter your first initial and last name</h1>
          <div className={styles.formSection}>
            <div className={styles.formInputSection}>
              <Field<string>
                component={InputFieldV2Adapter}
                inline
                isRequired
                data-testid="input-initial"
                id="input-initial"
                name="initial"
                type="text"
                label="First Initial"
                validate={composeValidators<string>(REQUIRED, EXACT_LENGTH(1))}
              />
              <Field<string>
                component={InputFieldV2Adapter}
                inline
                isRequired
                id="input-lastname"
                data-testid="input-lastname"
                name="lastName"
                type="text"
                label="Last Name"
                validate={composeValidators<string>(REQUIRED, MAX_LENGTH(100))}
              />
            </div>
            <ButtonV3 className={styles.submitButton} type="submit" disabled={invalid}>
              <span>Search</span>
            </ButtonV3>
          </div>
          <div className={styles.nameList}>{renderGuestSelection()}</div>
        </form>
      )}
    />
  );
};

export default GuestSearchForm;
